import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function fmtTime(ms) {
  if (!ms) return "0s";
  const s = Math.round(ms / 1000);
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return m > 0 ? `${m}m ${rem}s` : `${rem}s`;
}
function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function Analytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [tab, setTab] = useState("traffic"); // "traffic" | "activity"
  const [activity, setActivity] = useState(null);
  const [actAction, setActAction] = useState("");
  const [actUser, setActUser] = useState("");
  const [visitors, setVisitors] = useState(null);
  const [visitorDetail, setVisitorDetail] = useState(null); // null=closed, undefined=loading
  const [actDays, setActDays] = useState(7);
  const [actDaily, setActDaily] = useState(null);
  const isOwner = !!user?.is_owner;

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/analytics?days=${days}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [days]);

  // Log that this admin opened the Analytics section.
  useEffect(() => {
    api.post("/admin/activity/view", { section: "analytics" }).catch(() => {});
  }, []);

  useEffect(() => {
    if (tab !== "activity" || !isOwner) return;
    const params = new URLSearchParams();
    if (actAction) params.set("action", actAction);
    if (actUser) params.set("user_email", actUser);
    api.get(`/admin/activity?${params.toString()}`).then((r) => setActivity(r.data)).catch(() => setActivity({ activity: [], admins: [] }));
  }, [tab, actAction, actUser, isOwner]);

  // Per-visitor metrics for the selected traffic range.
  useEffect(() => {
    if (tab !== "traffic") return;
    api.get(`/admin/analytics/visitors?days=${days}`).then((r) => setVisitors(r.data.visitors)).catch(() => setVisitors([]));
  }, [tab, days]);

  // Daily per-admin activity totals (owner only).
  useEffect(() => {
    if (tab !== "activity" || !isOwner) return;
    api.get(`/admin/activity/daily?days=${actDays}`).then((r) => setActDaily(r.data.rows)).catch(() => setActDaily([]));
  }, [tab, actDays, isOwner]);

  const openVisitor = (vid) => {
    setVisitorDetail(undefined);
    api.get(`/admin/analytics/visitors/${vid}?days=${days}`).then((r) => setVisitorDetail(r.data)).catch(() => setVisitorDetail(null));
  };

  const doLogout = async () => { await logout(); navigate("/login", { replace: true }); };

  const ACTIONS = [
    "", "login", "logout", "create_post", "update_post", "publish_post",
    "approve_submission", "delete_post", "bulk_publish", "bulk_unpublish",
    "bulk_delete", "import_posts", "update_settings", "view",
  ];

  const ranges = [
    { v: 7, label: "Last 7 days" },
    { v: 30, label: "Last 30 days" },
    { v: 90, label: "Last 90 days" },
  ];
  const actRanges = [
    { v: 7, label: "7 days" },
    { v: 30, label: "30 days" },
    { v: 90, label: "90 days" },
  ];

  return (
    <div className="admin" data-testid="analytics-page">
      <header className="admin-header">
        <div className="admin-header-inner">
          <div className="admin-brand"><Link to="/admin" className="admin-link">&larr; Posts</Link><span> / Analytics</span></div>
          <div className="admin-header-actions">
            <span className="admin-user">{user?.email}</span>
            <button className="btn-ghost" onClick={doLogout}>Log out</button>
          </div>
        </div>
      </header>

      <main className="admin-main">
        <div className="admin-toolbar">
          <h1>Analytics</h1>
          {tab === "traffic" ? (
            <div className="range-tabs" data-testid="range-tabs">
              {ranges.map((r) => (
                <button
                  key={r.v}
                  className={`range-tab ${days === r.v ? "active" : ""}`}
                  onClick={() => setDays(r.v)}
                  data-testid={`range-${r.v}`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        {isOwner ? (
          <div className="range-tabs" style={{ marginBottom: 20 }} data-testid="analytics-tabs">
            <button className={`range-tab ${tab === "traffic" ? "active" : ""}`} onClick={() => setTab("traffic")} data-testid="tab-traffic">Traffic</button>
            <button className={`range-tab ${tab === "activity" ? "active" : ""}`} onClick={() => setTab("activity")} data-testid="tab-activity">Admin Activity</button>
          </div>
        ) : null}

        {tab === "activity" && isOwner ? (
          <>
          <section className="stat-block" data-testid="activity-daily">
            <div className="activity-daily-head">
              <h2>Daily activity per admin</h2>
              <div className="range-tabs">
                {actRanges.map((r) => (
                  <button
                    key={r.v}
                    className={`range-tab ${actDays === r.v ? "active" : ""}`}
                    onClick={() => setActDays(r.v)}
                    data-testid={`act-range-${r.v}`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
            <table className="admin-table" data-testid="activity-daily-table">
              <thead><tr><th>Date</th><th>Admin</th><th>Actions</th><th>Total</th></tr></thead>
              <tbody>
                {!actDaily ? (
                  <tr><td colSpan="4" className="admin-empty">Loading…</td></tr>
                ) : actDaily.length === 0 ? (
                  <tr><td colSpan="4" className="admin-empty">No activity in this range.</td></tr>
                ) : (
                  actDaily.map((r, i) => (
                    <tr key={`${r.date}-${r.user}`} data-testid={`activity-daily-row-${i}`}>
                      <td>{r.date}</td>
                      <td>{r.user}</td>
                      <td>{Object.entries(r.actions).sort((a, b) => b[1] - a[1]).map(([a, c]) => `${a.replace(/_/g, " ")} ×${c}`).join(", ")}</td>
                      <td>{r.total}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </section>
          <section className="stat-block" data-testid="activity-panel">
            <div className="activity-filters">
              <select value={actUser} onChange={(e) => setActUser(e.target.value)} data-testid="activity-filter-user">
                <option value="">All admins</option>
                {(activity?.admins || []).map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
              <select value={actAction} onChange={(e) => setActAction(e.target.value)} data-testid="activity-filter-action">
                {ACTIONS.map((a) => <option key={a} value={a}>{a === "" ? "All actions" : a.replace(/_/g, " ")}</option>)}
              </select>
            </div>
            <table className="admin-table" data-testid="activity-table">
              <thead><tr><th>When</th><th>Admin</th><th>Action</th><th>Item</th><th>Details</th><th>IP</th></tr></thead>
              <tbody>
                {!activity ? (
                  <tr><td colSpan="6" className="admin-empty">Loading…</td></tr>
                ) : activity.activity.length === 0 ? (
                  <tr><td colSpan="6" className="admin-empty">No activity yet.</td></tr>
                ) : (
                  activity.activity.map((r) => (
                    <tr key={r.id}>
                      <td>{fmtDate(r.created_at)}</td>
                      <td>{r.user_email || r.user_name}</td>
                      <td><span className={`act-badge act-${r.action}`}>{(r.action || "").replace(/_/g, " ")}</span></td>
                      <td>{r.target}</td>
                      <td>{r.detail}</td>
                      <td>{r.ip}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </section>
          </>
        ) : null}

        {tab === "traffic" ? (
          loading || !data ? (
            <p>Loading&hellip;</p>
          ) : (
          <>
            <div className="stat-cards">
              <div className="stat-card" data-testid="stat-views"><div className="stat-num">{data.total_views}</div><div className="stat-label">Total page views</div></div>
              <div className="stat-card" data-testid="stat-visitors"><div className="stat-num">{data.unique_visitors}</div><div className="stat-label">Unique visitors</div></div>
              <div className="stat-card" data-testid="stat-time"><div className="stat-num">{fmtTime(data.avg_time_ms)}</div><div className="stat-label">Avg. time on site</div></div>
              <div className="stat-card" data-testid="stat-help"><div className="stat-num">{data.help_bubble?.opens ?? 0}</div><div className="stat-label">Help bubble opens ({data.help_bubble?.unique_visitors ?? 0} unique)</div></div>
            </div>

            <section className="stat-block" data-testid="views-chart">
              <h2>Views over time</h2>
              <div className="chart-wrap">
                {(data.series || []).length === 0 ? (
                  <p className="admin-empty">No data yet.</p>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <AreaChart data={data.series} margin={{ top: 6, right: 12, left: -12, bottom: 0 }}>
                      <defs>
                        <linearGradient id="v" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#0a0a0a" stopOpacity={0.18} />
                          <stop offset="100%" stopColor="#0a0a0a" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                      <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} tick={{ fontSize: 11, fill: "#999" }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#999" }} width={30} />
                      <Tooltip />
                      <Area type="monotone" dataKey="views" stroke="#0a0a0a" strokeWidth={2} fill="url(#v)" />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </div>
            </section>

            <div className="stat-grid">
              <section className="stat-block">
                <h2>Pages &amp; posts</h2>
                <table className="admin-table">
                  <thead><tr><th>Page</th><th>Views</th><th>Visitors</th></tr></thead>
                  <tbody>
                    {data.pages.length === 0 ? <tr><td colSpan="3" className="admin-empty">No data yet.</td></tr> :
                      data.pages.map((p) => (
                        <tr key={p.path}><td>{p.title}<div className="admin-slug">{p.path}</div></td><td>{p.views}</td><td>{p.visitors}</td></tr>
                      ))}
                  </tbody>
                </table>
              </section>

              <section className="stat-block">
                <h2>Top referrers</h2>
                <table className="admin-table">
                  <thead><tr><th>Source</th><th>Views</th></tr></thead>
                  <tbody>
                    {data.referrers.length === 0 ? <tr><td colSpan="2" className="admin-empty">No data yet.</td></tr> :
                      data.referrers.map((r) => (<tr key={r.referrer}><td>{r.referrer}</td><td>{r.count}</td></tr>))}
                  </tbody>
                </table>
              </section>
            </div>

            <section className="stat-block">
              <h2>Recent visits</h2>
              <table className="admin-table" data-testid="recent-visits">
                <thead><tr><th>Time</th><th>Page</th><th>IP</th><th>Country</th><th>Referrer</th></tr></thead>
                <tbody>
                  {data.recent.length === 0 ? <tr><td colSpan="5" className="admin-empty">No visits yet.</td></tr> :
                    data.recent.map((v, i) => (
                      <tr key={i}>
                        <td>{fmtDate(v.created_at)}</td>
                        <td>{v.path}</td>
                        <td>{v.ip}</td>
                        <td>{[v.city, v.country].filter(Boolean).join(", ")}</td>
                        <td>{v.referrer}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </section>

            <section className="stat-block">
              <h2>Visitors</h2>
              <table className="admin-table" data-testid="visitors-table">
                <thead><tr><th>Visitor</th><th>Views</th><th>Time on site</th><th>Last seen</th><th>Top pages</th></tr></thead>
                <tbody>
                  {visitors === null ? (
                    <tr><td colSpan="5" className="admin-empty">Loading…</td></tr>
                  ) : visitors.length === 0 ? (
                    <tr><td colSpan="5" className="admin-empty">No visitors in this range.</td></tr>
                  ) : (
                    visitors.map((v, i) => (
                      <tr key={v.visitor_id} className="visitor-row" onClick={() => openVisitor(v.visitor_id)} data-testid={`visitor-row-${i}`}>
                        <td><code className="visitor-id">{v.visitor_id.slice(0, 8)}</code></td>
                        <td>{v.views}</td>
                        <td>{fmtTime(v.duration_ms)}</td>
                        <td>{fmtDate(v.last_seen)}</td>
                        <td>{v.top_paths.join(", ")}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              <p className="admin-hint">Click a visitor to see their full timeline.</p>
            </section>

            {visitorDetail !== null ? (
              <div className="admin-modal-overlay" onClick={() => setVisitorDetail(null)} data-testid="visitor-modal">
                <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
                  <div className="admin-modal-head">
                    <h2>Visitor {visitorDetail?.visitor_id ? visitorDetail.visitor_id.slice(0, 8) : ""}</h2>
                    <button className="admin-import-dismiss" onClick={() => setVisitorDetail(null)} aria-label="Close" data-testid="visitor-close">×</button>
                  </div>
                  {!visitorDetail ? (
                    <p>Loading…</p>
                  ) : (
                    <>
                      <p className="admin-modal-sub" data-testid="visitor-summary">
                        {visitorDetail.summary.views} views · {fmtTime(visitorDetail.summary.duration_ms)} on site · first seen {fmtDate(visitorDetail.summary.first_seen)}
                      </p>
                      <div className="admin-questions-list" data-testid="visitor-timeline">
                        {visitorDetail.timeline.length === 0 ? (
                          <p className="admin-questions-empty">No activity in this range.</p>
                        ) : (
                          visitorDetail.timeline.map((e, i) => (
                            <div className="admin-question-row" key={i} data-testid={`visitor-event-${i}`}>
                              <div className="admin-question-text">
                                {e.type === "event" ? `Event: ${e.event}` : e.type === "duration" ? `Stayed ${fmtTime(e.duration_ms)} on ${e.path || "page"}` : `Viewed ${e.path}`}
                              </div>
                              <div className="admin-question-meta">{fmtDate(e.created_at)}</div>
                            </div>
                          ))
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            ) : null}
          </>
          )
        ) : null}
      </main>
    </div>
  );
}
